import React, { memo, useCallback, useMemo, useState } from 'react'
import { GridColumnVisibilityModel, GridPaginationModel, GridRowParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { EmptyInvoices } from 'src/components/invoice/grid/EmptyInvoices'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  COMPOSED_INVOICES_GRID_COLUMNS_STORAGE_KEY,
  invoicePageSizeOptions,
} from 'src/constants/invoice/constants'
import { buildComposedInvoiceColumns } from 'src/constants/invoice/composedInvoiceGrid'
import { ComposedInvoicesTableFields } from 'src/constants/invoice/enums'
import type { ComposedInvoice, PendingInvoiceAction } from 'src/interfaces/invoice'
import { buildCustomerInvoiceDetailsPath } from 'src/pages/admin-dashboard/adminDashboardNav'
import {
  getComposedInvoices,
  getInvoicesLoading,
  getInvoicesPagination,
} from 'src/selectors/invoice'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'

interface Props {
  paginationModel: GridPaginationModel
  canManage: boolean
  onPaginationModelChange(model: GridPaginationModel): void
  onRunAction(action: PendingInvoiceAction): void
}

const getRowId = (row: ComposedInvoice) => {
  return row.id
}

const UNHIDEABLE_COLUMNS: Array<string> = [
  ComposedInvoicesTableFields.invoice,
  ComposedInvoicesTableFields.actions,
]

/** The `{ excluded, pinned }` shape `useGridColumns` itself persists under a storage key. */
interface StoredColumnsState {
  excluded?: Array<string>
  pinned?: Array<string>
}

const ComposedInvoicesTableComponent = ({
  paginationModel,
  canManage,
  onPaginationModelChange,
  onRunAction,
}: Props) => {
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const composedInvoices = useSelector(getComposedInvoices)
  const pagination = useSelector(getInvoicesPagination)
  const isLoading = useSelector(getInvoicesLoading)

  const composedInvoiceColumns = useMemo(() => {
    return buildComposedInvoiceColumns(onRunAction, canManage)
  }, [onRunAction, canManage])

  const { includedColumns } = useGridColumns(
    composedInvoiceColumns,
    COMPOSED_INVOICES_GRID_COLUMNS_STORAGE_KEY,
  )

  /**
   * `useGridColumns`'s `handleAddExcludedColumn` / `handleRemoveExcludedColumn` are not usable
   * here: both close over `excludedColumns` (deps `[excludedColumns]`), so calling several in one
   * synchronous `forEach` has every call read the same pre-update array and only the LAST write
   * survives — reachable through MUI's "Show/Hide All" toggle, which changes many columns in one
   * event. Mirrors the same fix in `PartnerInvoicesTable.tsx`: compute the complete next excluded
   * list in one pass, write it once to the SAME localStorage key/shape the hook itself would have
   * written, and mirror it into a local override the visibility model prefers when set — falling
   * back to the hook's `includedColumns` otherwise.
   */
  const [excludedColumnsOverride, setExcludedColumnsOverride] = useState<Array<string> | null>(null)

  const columnVisibilityModel = useMemo(() => {
    return composedInvoiceColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      if (excludedColumnsOverride !== null) {
        accumulator[column.field] = !excludedColumnsOverride.includes(column.field)

        return accumulator
      }

      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [composedInvoiceColumns, includedColumns, excludedColumnsOverride])

  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      const nextExcludedColumns = composedInvoiceColumns.reduce<Array<string>>(
        (accumulator, column) => {
          if (UNHIDEABLE_COLUMNS.includes(column.field)) {
            return accumulator
          }

          if (model[column.field] === false) {
            accumulator.push(column.field)
          }

          return accumulator
        },
        [],
      )

      setExcludedColumnsOverride(nextExcludedColumns)

      // Read fresh rather than trusting a mount-time snapshot of `pinned`, so a concurrently
      // written pin preference cannot be clobbered by this write.
      const persistentStateRaw = localStorage.getItem(COMPOSED_INVOICES_GRID_COLUMNS_STORAGE_KEY)
      const persistentState: StoredColumnsState | null = persistentStateRaw
        ? JSON.parse(persistentStateRaw)
        : null

      localStorage.setItem(
        COMPOSED_INVOICES_GRID_COLUMNS_STORAGE_KEY,
        JSON.stringify({
          excluded: nextExcludedColumns,
          pinned: persistentState?.pinned || [],
        }),
      )
    },
    [composedInvoiceColumns],
  )

  // A row opens the invoice's own page (its partner invoices), the way an insurance policy
  // opens from the Insurance tab. The page re-reads the row from the list it is derived from.
  const handleRowClick = useCallback(
    (params: GridRowParams<ComposedInvoice>) => {
      if (!customerId) {
        return
      }

      navigate(buildCustomerInvoiceDetailsPath(customerId, params.row.id))
    },
    [customerId, navigate],
  )

  // Server pagination. `rowCount` is required: without it the footer computes page count from
  // `rows.length` and the grid never offers page 2. `sortingMode` stays server (GridTable's
  // default) and every column is `sortable: false` — the endpoint has no sort parameter.
  return (
    <GridTable<ComposedInvoice>
      rows={composedInvoices}
      columns={composedInvoiceColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      paginationMode="server"
      rowCount={pagination.total ?? 0}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={invoicePageSizeOptions}
      // Rows open the details page, so they read as clickable — the grid's default cursor is
      // the text caret.
      className={tw`[& .MuiDataGrid-row]:cursor-pointer`}
      onRowClick={handleRowClick}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyInvoices}
      tableDataTestId="invoices-list-table"
      rowDataTestIdPrefix="invoices-list"
    />
  )
}

export const ComposedInvoicesTable = memo(ComposedInvoicesTableComponent)
